import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "@tanstack/react-router";
import { clsx } from "clsx";

type Placement = "bottom-start" | "bottom-end" | "right-start" | "top-start" | "top-end";

/**
 * Anchored popover for the topbar/sidebar (floating overlay — never inline). The panel is
 * PORTALED to <body> with fixed positioning computed from the trigger rect, so it is never
 * clipped by an `overflow-hidden` ancestor (e.g. the collapsed sidebar rail — that was the
 * "dropdown hidden when collapsed" bug). Closes on outside pointerdown, Escape, and route change.
 * Repositions on scroll/resize. `children` may be a `(close) => ReactNode` render-prop.
 */
export function Popover({
  trigger,
  children,
  placement = "bottom-end",
  panelClassName,
  ariaLabel = "Menu",
  open: controlledOpen,
  onOpenChange,
}: {
  trigger: (props: { open: boolean; toggle: () => void; setOpen: (v: boolean) => void }) => ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  placement?: Placement;
  panelClassName?: string;
  // Accessible name for the panel (contract §10): the panel is `role="dialog"`, so it MUST be
  // named — an unnamed dialog is announced as just "dialog". Defaults to "Menu" (every current
  // consumer is a nav menu/switcher) so no popover is ever an unnamed dialog; pass a precise
  // label at the call site.
  ariaLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const isControlled = controlledOpen !== undefined;

  const setOpen = useCallback((nextOpen: boolean) => {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChangeRef.current?.(nextOpen);
  }, [isControlled]);

  const PAD = 8;
  const HGAP = 0; // rail → right: flush against the sidebar edge (no gap)
  const VGAP = 8; // downward: match the topbar menus (Help/Account) offset — appshell-wide
  const compute = () => {
    const a = anchorRef.current?.firstElementChild ?? anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const pw = panelRef.current?.offsetWidth ?? 280;
    const ph = panelRef.current?.offsetHeight ?? 320;
    let top: number;
    let left: number;
    if (placement === "right-start") {
      top = r.top;
      left = r.right + HGAP;
    } else {
      // top-*: opens ABOVE the trigger (panel bottom edge sits VGAP above trigger top) — for a
      // bottom-anchored trigger (e.g. BottomTabBar), the bottom-* formula's `top` always exceeds
      // innerHeight and gets clamped to `innerHeight - ph - PAD`, which buries the panel over the
      // trigger itself. The existing PAD clamp below already keeps a top-anchored panel that runs
      // out of room from going off-screen at the top, so this reuses that same guard.
      const isTop = placement === "top-start" || placement === "top-end";
      top = isTop ? r.top - ph - VGAP : r.bottom + VGAP;
      const isEnd = placement === "bottom-end" || placement === "top-end";
      left = isEnd ? r.right - pw : r.left;
    }
    left = Math.max(PAD, Math.min(left, window.innerWidth - pw - PAD));
    top = Math.max(PAD, Math.min(top, window.innerHeight - ph - PAD));
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (open) compute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!anchorRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Keyboard close returns focus to the trigger (the panel is portaled to <body>, so
      // without this Escape strands focus on a detached node). Prefer the explicit menu
      // trigger ([aria-haspopup]) over the anchor's first button — split controls (e.g. the
      // workspace home/switch chip) put a nav button before the toggle.
      const anchor = anchorRef.current;
      (
        anchor?.querySelector<HTMLElement>("[aria-haspopup]") ??
        anchor?.querySelector<HTMLElement>("button, a[href], [tabindex]")
      )?.focus();
    };
    const onMove = () => compute();
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close when the route changes (a selection navigated).
  useEffect(() => { setOpen(false); }, [pathname, setOpen]);

  return (
    <div ref={anchorRef} className="contents">
      {trigger({ open, toggle: () => setOpen(!open), setOpen })}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={ariaLabel}
            style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            className={clsx(
              "z-[var(--z-popover)] origin-top overflow-hidden rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-[var(--surface-sunken)] shadow-[var(--shadow-lg)]",
              "motion-safe:animate-[popover-in_120ms_ease-out]",
              panelClassName,
            )}
          >
            {typeof children === "function" ? children(() => setOpen(false)) : children}
          </div>,
          document.body,
        )}
      <style>{`@keyframes popover-in{from{opacity:0;transform:scale(.97) translateY(-2px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
